import { effectiveAssignmentMinutes, SHIFT_END_UNVERIFIED_NOTE_MARKER } from "./shiftDuration";

export type LaborDayProvenanceSource =
  | "total_minutes"
  | "active_segment"
  | "unknown_duration"
  | "equivalent_day"
  | "planned";

export const LABOR_EQUIVALENT_DAY_MINUTES = 540;

export function isValidLaborMonth(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

/** Current Madrid civil month, matching workday-date semantics. */
export function getMadridCurrentMonthString(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid", year: "numeric", month: "2-digit",
  }).formatToParts(now);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  return `${year}-${month}`;
}

/** First/last Madrid civil day of a validated month. */
export function getLaborMonthRange(month: string): { firstDay: string; lastDay: string } {
  if (!isValidLaborMonth(month)) throw new Error("INVALID_LABOR_MONTH");
  const [year, monthNumber] = month.split("-").map(Number);
  const lastDayNumber = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return {
    firstDay: `${month}-01`,
    lastDay: `${month}-${String(lastDayNumber).padStart(2, "0")}`,
  };
}

export type LaborDayMinutesResolution = {
  /** Verified actual minutes only; null means the actual duration is unknown. */
  minutes: number | null;
  /** Legacy equivalent, kept outside actual totals and percentages. */
  equivalentMinutes: number;
  source: LaborDayProvenanceSource;
  durationVerified: boolean;
  planned: boolean;
};

/** Formats effective labor minutes; never used for economic amounts. */
export function formatLaborDurationMinutes(totalMinutes: number | null | undefined): string {
  const minutes = Number(totalMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return "0h 00m";
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return hours + "h " + String(rest).padStart(2, "0") + "m";
}

function hasValidStartTimeMarker(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) return false;
  const [hours, minutes, seconds] = [Number(match[1]), Number(match[2]), Number(match[3] ?? "0")];
  return hours <= 23 && minutes <= 59 && seconds <= 59;
}

/** One classification shared by the calendar, monthly views and CSV. */
export function resolveLaborDayMinutes(row: {
  totalMinutes?: unknown;
  status?: string;
  startTime?: unknown;
  endTime?: unknown;
  activeStartTime?: Date | string | null;
  date?: string | Date | null;
  shiftDate?: string | Date | null;
  shiftStatus?: string | null;
  shiftTotalMinutes?: number | null;
  shiftActiveStartAt?: Date | string | null;
  /** Server-projected marker; exact timestamps are kept out of the CAL-05 payload. */
  activeSegmentIncluded?: boolean;
  notes?: string | null;
  shiftEndUnverified?: boolean;
}, now: Date = new Date()): LaborDayMinutesResolution {
  const total = row?.totalMinutes == null || row.totalMinutes === "" ? null : Number(row.totalMinutes);
  const activeStart = row?.activeStartTime instanceof Date
    ? row.activeStartTime
    : row?.activeStartTime ? new Date(row.activeStartTime) : null;
  const hasActiveMarker = Boolean(activeStart && !Number.isNaN(activeStart.getTime()));
  const hasStartTimeMarker = hasValidStartTimeMarker(row?.startTime);
  const shiftEndUnverified = row?.shiftEndUnverified === true
    || Boolean(row?.notes?.includes(SHIFT_END_UNVERIFIED_NOTE_MARKER));
  if (shiftEndUnverified) {
    return { minutes: null, equivalentMinutes: 0, source: "unknown_duration", durationVerified: false, planned: false };
  }
  const planned = row?.status === "assigned" && !(Number.isFinite(total) && total! > 0)
    && !hasActiveMarker && !hasStartTimeMarker;
  if (planned) {
    return { minutes: 0, equivalentMinutes: 0, source: "planned", durationVerified: false, planned: true };
  }

  const baseMinutes = Number.isFinite(total) ? Math.max(0, Math.round(total!)) : 0;
  if (row?.status === "working" && row?.activeSegmentIncluded === true && Number.isFinite(total)) {
    return {
      minutes: baseMinutes,
      equivalentMinutes: 0,
      source: "active_segment",
      durationVerified: true,
      planned: false,
    };
  }
  if (row?.status === "working" && hasActiveMarker) {
    const effectiveMinutes = effectiveAssignmentMinutes({
      status: row.status,
      totalMinutes: Number.isFinite(total) ? total : null,
      activeStartTime: activeStart,
      date: row.date,
      shiftDate: row.shiftDate,
      shiftStatus: row.shiftStatus,
      shiftTotalMinutes: row.shiftTotalMinutes,
      shiftActiveStartAt: row.shiftActiveStartAt,
    }, now);
    return {
      minutes: effectiveMinutes,
      equivalentMinutes: 0,
      source: "active_segment",
      durationVerified: true,
      planned: false,
    };
  }
  if (Number.isFinite(total) && total! > 0) {
    return { minutes: Math.round(total!), equivalentMinutes: 0, source: "total_minutes", durationVerified: true, planned: false };
  }
  // An explicit zero with durable start evidence is measured. A null with the
  // same evidence proves activity, but does not prove its duration.
  if (Number.isFinite(total) && total === 0 || total === null) {
    if (total === 0 && (hasActiveMarker || hasStartTimeMarker)) {
      return { minutes: 0, equivalentMinutes: 0, source: "total_minutes", durationVerified: true, planned: false };
    }
    if (total === null && (hasActiveMarker || hasStartTimeMarker)) {
      return { minutes: null, equivalentMinutes: 0, source: "unknown_duration", durationVerified: false, planned: false };
    }
    if (row?.status === "paused") {
      return { minutes: null, equivalentMinutes: 0, source: "unknown_duration", durationVerified: false, planned: false };
    }
    if (row?.status !== "assigned") {
      return { minutes: null, equivalentMinutes: LABOR_EQUIVALENT_DAY_MINUTES, source: "equivalent_day", durationVerified: false, planned: false };
    }
    return { minutes: 0, equivalentMinutes: 0, source: "planned", durationVerified: false, planned: true };
  }

  // startTime/endTime may bound a day containing one or more pauses. They are
  // useful as context only; elapsed wall time is never effective labor time.
  return {
    minutes: null,
    equivalentMinutes: LABOR_EQUIVALENT_DAY_MINUTES,
    source: "equivalent_day",
    durationVerified: false,
    planned: false,
  };
}
